import { cva } from 'class-variance-authority';
import { focusRing, transitionBase, uiMarker, uiMarkerLayout } from './shared';

/**
 * CHAT — a conversation on a phone: what the person sent, in a bubble at
 * the right, and the box along the bottom they type into. The other side's
 * words are plain Text on the page, so only the person's own need a shape.
 *
 * No typing dots, no pulse, no blink. The one thing that moves is the
 * working line: the Relay arrow flies through its tile while the step it is
 * on is said in words.
 */

/** The bubble: a wash of ink, squared off at the corner nearest the edge. */
export const chatBubbleRecipe = cva(
  [
    uiMarker,
    'max-w-[85%] self-end rounded-xl rounded-br-sm bg-foreground/5',
    'font-sans text-body-lg text-foreground',
    'whitespace-pre-wrap break-words',
  ],
  {
    variants: {
      /** A paste is a fixed-width card, like a file, not a run of words. */
      paste: { true: 'w-72 p-1.5', false: 'px-3.5 py-2.5' },
    },
    defaultVariants: { paste: false },
  }
);

/**
 * The pasted block's toggle: a page tile, then "Pasted call" over its first
 * line, then a caret that turns over when the text is open.
 */
export const chatPasteToggleRecipe = cva([
  uiMarker,
  'flex w-full min-w-0 items-center gap-3 rounded-lg p-1 text-left',
  '[&>svg]:size-icon-sm [&>svg]:shrink-0 [&>svg]:text-muted-foreground',
  '[&>svg]:transition-transform aria-expanded:[&>svg]:rotate-180',
  focusRing,
]);

/** The tile the page icon sits on, so the paste reads as a document. */
export const chatPasteTileRecipe = cva([
  uiMarker,
  'grid size-9 shrink-0 place-items-center rounded-md bg-background',
  'text-muted-foreground [&_svg]:size-icon-sm',
]);

/** "Pasted call" over the first line, taking the room left in the row. */
export const chatPasteNameRecipe = cva([
  uiMarker,
  'flex min-w-0 flex-1 flex-col',
]);

/** The pasted text itself, opened under the toggle. */
export const chatPasteBodyRecipe = cva([
  uiMarker,
  'mx-1 mb-1 mt-1.5 border-t border-border pt-2',
  'whitespace-pre-wrap break-words font-sans text-body-sm text-muted-foreground',
]);

/**
 * The composer's track: a faint wash around a white inner pill, as the
 * phone's status control draws its track, so the box reads as one control.
 */
export const chatComposerRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex w-full rounded-[1.625rem] bg-foreground/5 p-0.5',
]);

export const chatComposerInnerRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex min-w-0 flex-1 items-end gap-1 rounded-3xl bg-background p-1',
]);

/**
 * The box: no border of its own, the pill is its border. It grows with
 * what is typed up to four lines, then scrolls.
 */
export const chatComposerInputRecipe = cva([
  uiMarker,
  'max-h-24 min-h-10 min-w-0 flex-1 resize-none bg-transparent px-2.5 py-2',
  'font-sans text-body-lg text-foreground placeholder:text-muted-foreground',
  'outline-none',
  transitionBase,
]);

/**
 * The line the chat shows while it works: the Relay arrow, then what it is
 * doing now.
 */
export const chatWorkingRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex items-center gap-3',
]);

/**
 * The window the arrow flies through: no fill of its own, it only clips the
 * arrow as it leaves at the top right and comes back from the bottom left.
 */
export const chatWorkingWindowRecipe = cva([
  uiMarker,
  'grid size-5 shrink-0 place-items-center overflow-hidden',
]);

/** The Relay arrow itself: the site icon's artwork, in its own green. */
export const chatWorkingArrowRecipe = cva([
  uiMarker,
  'size-5 animate-arrow-launch',
]);

/** Each step arrives once from below; the key change replays it. */
export const chatWorkingStepRecipe = cva([uiMarker, 'animate-rise-in']);
